import clsx from 'clsx';
import { PropsWithChildren, CSSProperties } from 'react';
import { getImageUrl } from '@/utils/images';
import { getTailwindColor } from '@/utils/colors';
import { WAVE_OVERLAP_VW } from '../footer/components/wave';

type GridTemplate = {
    width: '1/1' | '1/2' | '1/3' | '2/3' | '1/4';
};

type VerticalPadding = 'big' | 'medium' | 'none' | 'legacy-value' | 'legacy-value-was-none' | 'legacy-value-was-big';

export type SectionProps = {
    backgroundType: null | 'image' | 'color';
    backgroundImageRepeat?: boolean;
    backgroundImageHighRes?: boolean;
    verticalPadding?: VerticalPadding;
    paddingTop?: VerticalPadding;
    paddingBottom?: VerticalPadding;
    mobileDesktopFilter: 'all' | 'mobile' | 'desktop';
    template: GridTemplate[];
    templateID: number;
    hash: string;
    equalHeight?: boolean;
    gridGapSize?: 'normal' | 'small' | 'none';
    removeGridGap?: boolean /** @deprecated Use gridGapSize instead */;
    wave?: boolean;
    waveColor?: string;
    anchorEnable?: boolean;
    anchorName?: string;
    removePadding?: boolean;
    /**
     * @deprecated Not settable in WP since !2686. Still set on existing sections — extend-row.js
     * migrates them to `sectionWidth` on parse, so keep this until prod content is clean (likely never to happen).
     */
    fullWidth?: 'stretch' | 'noStretch';
    /** From WP's `sectionWidth`. Takes precedence over `fullWidth`. */
    width?: 'full' | 'constrained';
    verticalAlignment?: 'center' | 'bottom';
    allowOverflow?: boolean;
    linkColor?: {
        key: string;
        color: string;
    };
    focus?: {
        x: null | number;
        y: null | number;
    };
    backgroundValue?:
        | string
        | {
              height: number;
              width: number;
              url: string;
              __isimage: boolean;
          };
    isLast?: boolean;
};

export const sectionHasImageBackground = (section: Pick<SectionProps, 'backgroundType' | 'backgroundValue'>): boolean =>
    section.backgroundType === 'image' && !!section.backgroundValue && typeof section.backgroundValue !== 'string';

function calculateGridColumns(templates: GridTemplate[]) {
    const strTemplates = templates.map(({ width }) => width).join('-');

    switch (strTemplates) {
        case '1/2-1/2':
            return 'md:r-grid-cols-2';
        case '1/3-2/3':
            return 'md:r-grid-cols-[1fr_2fr]';
        case '2/3-1/3':
            return 'md:r-grid-cols-[2fr_1fr]';
        case '1/3-1/3-1/3':
            return 'md:r-grid-cols-3';
        case '1/4-1/2-1/4':
            return 'md:r-grid-cols-[1fr_2fr_1fr]';
        case '1/4-1/4-1/4-1/4':
            return 'md:r-grid-cols-4';
    }

    return 'md:r-grid-cols-1';
}

export const Section = ({ children, isLast = false, ...props }: PropsWithChildren<SectionProps>) => {
    // Support both new separate paddingTop/paddingBottom and legacy verticalPadding
    const paddingTop = props.paddingTop ?? (props.verticalPadding?.startsWith('legacy-value') ? 'medium' : props.verticalPadding) ?? 'medium';
    const paddingBottom = props.paddingBottom ?? (props.verticalPadding?.startsWith('legacy-value') ? 'medium' : props.verticalPadding) ?? 'medium';

    // Support both new width and legacy fullWidth (width takes precedence if defined)
    const isFullWidth = props.width !== undefined ? props.width === 'full' : props.fullWidth === 'stretch';

    const classNames = clsx('r-relative r-grid styled-section-inner r-bg-transparent', calculateGridColumns(props.template), {
        'r-items-stretch': props?.equalHeight && !props.verticalAlignment,
        'r-items-start': !props?.equalHeight && !props.verticalAlignment,
        'r-gap-8': props?.gridGapSize === 'normal' || (!props?.gridGapSize && !props?.removeGridGap),
        'r-gap-4': props?.gridGapSize === 'small',
        'r-pt-0': paddingTop === 'none',
        'r-pt-8': paddingTop === 'medium',
        'r-pt-12': paddingTop === 'big',
        'r-pb-0': paddingBottom === 'none',
        'r-pb-8': paddingBottom === 'medium',
        'r-pb-12': paddingBottom === 'big',
        'md:r-container': !isFullWidth,
        'r-px-4 md:r-px-8': !props?.removePadding,
        'r-items-end': props.verticalAlignment === 'bottom',
        'r-items-center': props.verticalAlignment === 'center',
        'r-min-h-[350px] md:r-min-h-[540px]': props.wave,
    });

    const sectionPtMap = { none: '0px', medium: '32px', big: '48px' } as const;
    const sectionPbMap = { none: '0px', medium: '32px', big: '48px' } as const;

    const style: CSSProperties = {};
    const bgImageStyle: CSSProperties = {};
    if (props.backgroundType === 'image' && props.backgroundValue && typeof props.backgroundValue !== 'string') {
        if (props.backgroundImageRepeat) {
            bgImageStyle.backgroundImage = `url(${getImageUrl(props.backgroundValue.url, '225xAUTO')})`; // We could use 'original' but this is not cached by the image service so is just super slow
        } else {
            const size = props.backgroundImageHighRes ? '2560xAUTO' : '1920xAUTO';
            style.backgroundImage = `url(${getImageUrl(props.backgroundValue.url, size)})`;
        }
    }

    if (props.focus?.x && props.focus?.y) {
        style.backgroundPositionX = `${props.focus?.x * 100}%`;
        style.backgroundPositionY = `${props.focus?.y * 100}%`;
    }

    const needsWavePadding = isLast && sectionHasImageBackground(props);

    return (
        <div
            id={props.anchorName}
            data-component="section"
            style={bgImageStyle}
            className={clsx('scrollbar-hide r-relative r-w-full', getTailwindColor(props.backgroundValue, 'bg'), {
                'r-overflow-hidden': !props.allowOverflow,
                'r-bg-cover r-bg-center r-bg-no-repeat': !props.backgroundImageRepeat,
                'r-bg-repeat': props.backgroundImageRepeat,
                [`link-color-${props?.linkColor?.key?.replace('#', '')}`]: props?.linkColor?.key,
            })}
        >
            {props?.linkColor?.key && (
                <style
                    type="text/css"
                    dangerouslySetInnerHTML={{
                        __html: `.link-color-${props?.linkColor?.key?.replace('#', '')} a:not(.resurs-button):not(.carousel-item) { color: ${props?.linkColor?.color} !important; }`,
                    }}
                />
            )}

            <div
                style={style}
                className={clsx('r-absolute r-top-0 r-w-full', {
                    'r-bottom-0': !props.wave,
                    'r-h-[380px] md:r-h-[450px] xl:r-h-[520px]': props.wave,
                    'r-bg-cover r-bg-top r-bg-no-repeat': !props.backgroundImageRepeat,
                    'r-bg-repeat': props.backgroundImageRepeat,
                })}
            />
            {props.wave && (
                <div className="r-pointer-events-none r-absolute r-bottom-0 r-right-0 r-top-[280px] r-w-full md:r-top-[350px]">
                    <svg viewBox="0 -1 1441 95" xmlns="http://www.w3.org/2000/svg" className={`${getTailwindColor(props.waveColor, 'fill')}`}>
                        <path d="M0 0V95H1441V0C1225.69 60.1229 980.57 94.1402 720.5 94.1402C460.43 94.1402 215.306 60.1229 0 0Z" />
                    </svg>
                    <div className={clsx('-r-mt-[1px] r-h-full', getTailwindColor(props.waveColor, 'bg'))} />
                </div>
            )}
            <div
                className={classNames}
                style={
                    {
                        '--section-pt': sectionPtMap[paddingTop as keyof typeof sectionPtMap] ?? '32px',
                        ...(needsWavePadding
                            ? { paddingBottom: `calc(${sectionPbMap[paddingBottom as keyof typeof sectionPbMap] ?? '0px'} + ${WAVE_OVERLAP_VW})` }
                            : null),
                    } as CSSProperties
                }
            >
                {children}
            </div>
        </div>
    );
};
